import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { buildChronicleUrls } from '../utils/mediaPaths';

const FETCH_COOLDOWN_MS = 8000;
const DEFAULT_RATE_LIMIT_BACKOFF_MS = 30000;

export const fetchChronicles = createAsyncThunk(
  'hero/fetchChronicles',
  async (arg, { rejectWithValue }) => {
    // arg can be a plain string or { apiBase }
    const raw = (typeof arg === 'string') ? arg : arg?.apiBase;
    const base = (raw || '').replace(/\/$/, '');
    const urls = [
      `${base}/api/chronicle/audio-list?limit=100`,
      `${base}/api/chronicle-lite/audio-list?limit=100`,
    ];

    try {
      for (let i = 0; i < urls.length; i += 1) {
        const url = urls[i];
        const res = await fetch(url, { credentials: 'omit' });
        if (res.ok) {
          const data = await res.json();
          return { data, apiBase: base, fetchedAt: Date.now() };   // pass through for mapper
        }

        const retryAfterHeader = Number.parseInt(res.headers.get('retry-after') || '', 10);
        const errorPayload = {
          status: res.status,
          message: `HTTP ${res.status} on ${url}`,
          retryAfterSeconds: Number.isFinite(retryAfterHeader) && retryAfterHeader > 0
            ? retryAfterHeader
            : null,
        };

        // Do not fan out to fallback endpoints on rate limits.
        // Additional attempts only amplify 429 spam and pressure.
        if (res.status === 429) {
          return rejectWithValue(errorPayload);
        }

        // Non-429 failures can try the next endpoint as a fallback.
        const hasNext = i < urls.length - 1;
        if (hasNext) continue;
        return rejectWithValue(errorPayload);
      }

      return rejectWithValue({ message: 'Unable to fetch chronicles from available endpoints.' });
    } catch (e) {
      return rejectWithValue(e.message || String(e));
    }
  },
  {
    condition: (arg, { getState }) => {
      const heroState = getState()?.hero;
      if (!heroState) return true;
      if (heroState.status === 'loading') return false;

      const forceRefresh = Boolean(arg && typeof arg === 'object' && arg.force);
      if (forceRefresh) return true;

      const now = Date.now();
      if (heroState.nextFetchAllowedAt && now < heroState.nextFetchAllowedAt) return false;
      if (heroState.lastFetchedAt && (now - heroState.lastFetchedAt) < FETCH_COOLDOWN_MS) return false;

      return true;
    }
  },
);

// --- robust normalizer ---
function toList(raw) {
  if (!raw) return [];

  // Direct array?  e.g. [{url, key, createdAt, ...}, ...]
  if (Array.isArray(raw)) return raw;

  // Common wrappers
  const candidates = (
    raw.chronicles ||
    raw.audioFiles ||
    raw.items ||
    raw.files ||
    raw.audios ||
    raw.list ||
    []
  );

  return Array.isArray(candidates) ? candidates : [];
}

function pick(v, ...keys) {
  for (const k of keys) if (v?.[k] != null) return v[k];
  return undefined;
}

function normalizeBasename(input) {
  const value = String(input || '');
  if (!value) return '';
  const clean = value.split('?')[0].split('#')[0];
  const last = clean.split('/').filter(Boolean).pop() || clean;
  return last.replace(/\.(mp3|json)$/i, '');
}

// build absolute URL if backend sent a relative path
// derive timeOfDay if missing
function inferCardinal(dateIso) {
  try {
    const h = new Date(dateIso).getHours();
    if (h < 6) return 'Midnight';
    if (h < 12) return 'Morning';
    if (h < 18) return 'Noon';
    return 'Evening';
  } catch { return 'Evening'; }
}

const initialState = {
  availableChronicles: [],
  selectedChronicleId: '',
  status: 'idle',
  error: null,
  lastFetchedAt: 0,
  nextFetchAllowedAt: 0,
  socketConnected: false,
  activeFrequency: null,
  isSlicingProtocolOpen: false,
};

const heroSlice = createSlice({
  name: 'hero',
  initialState,
  reducers: {
    setSelectedChronicleId(state, action) { state.selectedChronicleId = action.payload; },
    setSocketConnected(state, action) { state.socketConnected = !!action.payload; },
    setActiveFrequency(state, action) { state.activeFrequency = action.payload; },
    setSlicingProtocolOpen(state, action) { state.isSlicingProtocolOpen = !!action.payload; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChronicles.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchChronicles.fulfilled, (state, action) => {
        const raw = action.payload?.data;

        const list = toList(raw)
          .map((item) => {
            // Respect the source filename EXACTLY as the backend provides it.
            // It may be:
            //  - a bare id: "2025-09-23_noon_chronicle_2025-09-23T20-28-45-795Z"
            //  - or already with ".mp3"
            let nameLike = pick(item, 'filename', 'name', 'key', 'object', 'id', 'path') || '';

            // If backend omitted a name-like field but sent a URL, infer basename from the URL.
            const providedUrl = pick(item, 'url', 'audioUrl', 'mp3Url', 'src');
            if ((!nameLike || typeof nameLike !== 'string') && typeof providedUrl === 'string' && providedUrl.length) {
              nameLike = providedUrl;
            }

            const normalizedBase = normalizeBasename(nameLike);
            const { mp3Url, jsonUrl, altMp3Url, altJsonUrl, filename, basename } = buildChronicleUrls(normalizedBase);

            // Prefer backend-provided absolute/relative URL when present (e.g., same-origin URLs)
            const normalizedProvidedUrl = (typeof providedUrl === 'string' && providedUrl.length)
              ? providedUrl
              : '';
            const primaryUrl = normalizedProvidedUrl
              ? normalizedProvidedUrl
              : mp3Url;

            // Only trust an explicit .json URL from the backend directly. Do NOT derive
            // one by swapping a .mp3 URL's extension in place — chronicle JSON lives under
            // a different GCS prefix with a different (prefix-stripped) basename than the
            // audio file (see buildChronicleUrls' canonicalChronicleId), so that guess is
            // wrong and was silently overriding the correct jsonUrl computed below.
            const derivedJsonUrl = (typeof providedUrl === 'string' && /\.json$/i.test(providedUrl.split('?')[0].split('#')[0]))
              ? providedUrl.split('?')[0].split('#')[0]
              : null;

            // Hard guard: never allow legacy sample_* entries through, even if only URL is present.
            const sampleHint = (
              (typeof nameLike === 'string' && /^sample_/i.test(nameLike)) ||
              (typeof basename === 'string' && /^sample_/i.test(basename)) ||
              (typeof primaryUrl === 'string' && /\/(?:audio|sounds)\/sample_/i.test(primaryUrl))
            );
            if (sampleHint) return null;

            const rawTs = pick(item,'createdAt','timeCreated','updatedAt','lastModified','timestamp') || null;
            const meta  = pick(item,'metadata','meta','attributes') || {};

            const isField = Boolean(
              item.isFieldTransmission ||
              meta.isFieldTransmission ||
              meta.sourceType === 'venue' ||
              meta.sourceType === 'artist' ||
              meta.sourceType === 'field' ||
              meta.type === 'field_transmission' ||
              (typeof nameLike === 'string' && (
                /^transmission_/i.test(nameLike) ||
                /_transmission_/i.test(nameLike) ||
                /^field_/i.test(nameLike) ||
                /^venue-/i.test(nameLike) ||
                /^artist-/i.test(nameLike)
              )) ||
              (typeof basename === 'string' && (
                /^transmission_/i.test(basename) ||
                /_transmission_/i.test(basename) ||
                /^field_/i.test(basename) ||
                /^venue-/i.test(basename) ||
                /^artist-/i.test(basename)
              ))
            );

            const matchSlot = String(basename || nameLike).match(/(?:^|\d{4}-\d{2}-\d{2}_)(morning|noon|midday|evening|midnight)(?:_|$)/i);
            const rawCardinal = item.cardinalTime || item.cardinalSlot || meta.cardinalTime || meta.cardinalSlot || (!isField ? item.timeOfDay : null) || '';
            let cardinalSlot = null;
            if (!isField) {
              if (matchSlot) {
                cardinalSlot = matchSlot[1].toLowerCase();
              } else if (typeof rawCardinal === 'string' && ['morning', 'noon', 'midday', 'evening', 'midnight'].includes(rawCardinal.toLowerCase())) {
                cardinalSlot = rawCardinal.toLowerCase();
              }
            }
            if (cardinalSlot === 'midday') cardinalSlot = 'noon';
            const cardinalTime = cardinalSlot ? (cardinalSlot.charAt(0).toUpperCase() + cardinalSlot.slice(1)) : null;
            const isCardinal = Boolean(cardinalTime);
            const timeOfDay = isCardinal ? cardinalTime : (isField ? null : (pick(item, 'timeOfDay') || null));

            // Canonical date & timestamp extraction from filename to prevent translation renders from mutating broadcast date
            const nameForTs = String(basename || filename || nameLike || item.id || item.key || '');
            let ts = rawTs;
            const isoMatch = nameForTs.match(/(\d{4}-\d{2}-\d{2})T(\d{2})[-:](\d{2})[-:](\d{2})(?:[-.](\d{3}))?Z/i);
            if (isoMatch) {
              const [_, ymd, h, m, s, ms] = isoMatch;
              ts = `${ymd}T${h}:${m}:${s}.${ms || '000'}Z`;
            } else {
              const cardinalDateMatch = nameForTs.match(/^(\d{4}-\d{2}-\d{2})_(morning|noon|midday|evening|midnight)_/i);
              if (cardinalDateMatch) {
                const [_, ymd, slot] = cardinalDateMatch;
                const slotHours = {
                  midnight: '04:00:00.000Z',
                  morning:  '10:00:00.000Z',
                  noon:     '16:00:00.000Z',
                  midday:   '16:00:00.000Z',
                  evening:  '22:00:00.000Z',
                };
                if (rawTs && !Number.isNaN(new Date(rawTs).getTime()) && new Date(rawTs).toISOString().slice(0, 10) === ymd) {
                  ts = new Date(rawTs).toISOString();
                } else {
                  ts = `${ymd}T${slotHours[slot.toLowerCase()] || '12:00:00.000Z'}`;
                }
              }
            }

            let canonicalBase = String(basename || '').replace(/(?:_|-)(?:fr-ca|fr|qc|en)$/i, '');
            if (isField) {
              canonicalBase = canonicalBase
                .replace(/^(\d{4}-\d{2}-\d{2})_(midnight|morning|noon|midday|evening)_transmission_/i, 'transmission_')
                .replace(/^(\d{4}-\d{2}-\d{2})_(midnight|morning|noon|midday|evening)_/i, '');
              if (!canonicalBase.startsWith('transmission_') && (canonicalBase.startsWith('venue-') || canonicalBase.startsWith('artist-') || canonicalBase.startsWith('field-'))) {
                canonicalBase = `transmission_${canonicalBase}`;
              }
            }

            const itemLang = item.language || (basename && /(?:_|-)(?:fr-ca|qc|fr)$/i.test(basename) ? 'fr-CA' : 'en');
            let initialCarriers = item.carriers || meta.carriers || (item.carrier ? { [itemLang]: item.carrier } : null) || {};
            if (itemLang === 'fr-CA') {
              initialCarriers['fr-CA'] = initialCarriers['fr-CA'] || { audioUrl: primaryUrl, audioReady: true, status: 'rendered' };
            } else {
              initialCarriers['en'] = initialCarriers['en'] || { audioUrl: primaryUrl, audioReady: true, status: 'rendered' };
            }

            return {
              key: canonicalBase,    // stable key = canonical basename
              url: primaryUrl,       // prefer backend URL (can be same-origin), else GCS
              jsonUrl: derivedJsonUrl || jsonUrl, // mirror JSON (same basename)
              filename,              // canonical mp3 filename
              basename: canonicalBase, // without extension
              rawBasename: basename,
              createdAt: ts,
              timeOfDay,
              cardinalTime,
              cardinalSlot,
              isCardinal,
              isFieldTransmission: isField,
              language: itemLang,
              carriers: initialCarriers,
              youtubeVideoId: item.youtubeVideoId || meta.youtubeVideoId || null,
              youtubeUrl: item.youtubeUrl || meta.youtubeUrl || null,
              metadata: meta,
              candidates: Array.from(new Set([
                primaryUrl,
                mp3Url,
                altMp3Url,
                ...(Array.isArray(item.candidates) ? item.candidates : [])
              ].filter(Boolean))),
              jsonCandidates: Array.from(new Set([
                derivedJsonUrl,
                jsonUrl,
                altJsonUrl,
                ...(Array.isArray(item.jsonCandidates) ? item.jsonCandidates : [])
              ].filter(Boolean))),
            };
          })
          .filter(Boolean)
          .filter(it => !!it?.url);

        const dedupeMap = new Map();
        for (const it of list) {
          if (!dedupeMap.has(it.key)) {
            dedupeMap.set(it.key, it);
          } else {
            const existing = dedupeMap.get(it.key);
            const mergedCandidates = Array.from(new Set([...(existing.candidates || []), ...(it.candidates || [])]));
            const mergedJsonCandidates = Array.from(new Set([...(existing.jsonCandidates || []), ...(it.jsonCandidates || [])]));
            const mergedCarriers = { ...(existing.carriers || {}), ...(it.carriers || {}) };

            if (it.language === 'fr-CA' || /(?:_|-)(?:fr-ca|qc|fr)$/i.test(it.rawBasename || '')) {
              mergedCarriers['fr-CA'] = mergedCarriers['fr-CA'] || { audioUrl: it.url, audioReady: true, status: 'rendered' };
            }
            if (it.language === 'en' && !/(?:_|-)(?:fr-ca|qc|fr)$/i.test(it.rawBasename || '')) {
              mergedCarriers['en'] = mergedCarriers['en'] || { audioUrl: it.url, audioReady: true, status: 'rendered' };
            }
            if (existing.language === 'fr-CA' || /(?:_|-)(?:fr-ca|qc|fr)$/i.test(existing.rawBasename || '')) {
              mergedCarriers['fr-CA'] = mergedCarriers['fr-CA'] || { audioUrl: existing.url, audioReady: true, status: 'rendered' };
            }
            if (existing.language === 'en' && !/(?:_|-)(?:fr-ca|qc|fr)$/i.test(existing.rawBasename || '')) {
              mergedCarriers['en'] = mergedCarriers['en'] || { audioUrl: existing.url, audioReady: true, status: 'rendered' };
            }

            const mergedYoutubeVideoId = existing.youtubeVideoId || it.youtubeVideoId || null;
            const mergedYoutubeUrl = existing.youtubeUrl || it.youtubeUrl || null;

            const preferItForBase = (it.language === 'en' && existing.language === 'fr-CA') ||
              (it.rawBasename?.startsWith('transmission_') && !existing.rawBasename?.startsWith('transmission_'));
            const baseRecord = preferItForBase ? it : existing;
            const otherRecord = preferItForBase ? existing : it;

            const toMs = (val) => {
              if (!val) return 0;
              const d = new Date(val);
              return Number.isNaN(d.getTime()) ? 0 : d.getTime();
            };
            const canonicalCreatedAt = (toMs(existing.createdAt) > 0 && toMs(it.createdAt) > 0)
              ? (toMs(existing.createdAt) < toMs(it.createdAt) ? existing.createdAt : it.createdAt)
              : (existing.createdAt || it.createdAt);

            const enAudio = mergedCarriers['en']?.audioUrl || mergedCandidates.find(c => !/(?:_|-)(?:fr-ca|qc|fr)/i.test(c));
            const primaryDefaultUrl = enAudio || baseRecord.url || otherRecord.url;

            dedupeMap.set(it.key, {
              ...otherRecord,
              ...baseRecord,
              url: primaryDefaultUrl,
              createdAt: canonicalCreatedAt,
              candidates: mergedCandidates,
              jsonCandidates: mergedJsonCandidates,
              carriers: mergedCarriers,
              youtubeVideoId: mergedYoutubeVideoId,
              youtubeUrl: mergedYoutubeUrl,
            });
          }
        }

        const finalList = Array.from(dedupeMap.values())
          .sort((a, b) => (new Date(b.createdAt || 0)) - (new Date(a.createdAt || 0)));
        state.availableChronicles = finalList;
        state.status = 'succeeded';
        state.error = null;
        state.lastFetchedAt = action.payload?.fetchedAt || Date.now();
        state.nextFetchAllowedAt = 0;
        const current = state.selectedChronicleId;
        const isSample = current ? /^sample_/i.test(current) : false;
        const exists = current && list.some((it) => it.key === current);
        if (isSample || !exists) {
          state.selectedChronicleId = list.length ? list[0].key : '';
        }
      })
      .addCase(fetchChronicles.rejected, (state, action) => {
        // Rejected due to thunk condition (cooldown/in-flight guard); ignore.
        if (action.meta?.condition) return;

        const payload = (action.payload && typeof action.payload === 'object') ? action.payload : null;
        const statusCode = payload?.status || null;
        const fallbackError = action.payload || action.error?.message || 'Unknown error';
        const message = payload?.message || fallbackError;

        if (statusCode === 429) {
          const retryAfterMs = Math.max(
            DEFAULT_RATE_LIMIT_BACKOFF_MS,
            (payload?.retryAfterSeconds || 0) * 1000,
          );
          state.nextFetchAllowedAt = Date.now() + retryAfterMs;

          // Keep stale list usable if we already have one.
          if (Array.isArray(state.availableChronicles) && state.availableChronicles.length > 0) {
            state.status = 'succeeded';
            state.error = null;
            return;
          }

          state.status = 'failed';
          state.error = `Rate limited while loading chronicles. Retry in ${Math.ceil(retryAfterMs / 1000)}s.`;
          return;
        }

        // Non-rate-limit failures: keep stale data when available.
        if (Array.isArray(state.availableChronicles) && state.availableChronicles.length > 0) {
          state.status = 'succeeded';
          state.error = null;
          return;
        }

        state.status = 'failed';
        state.error = message;
        state.availableChronicles = [];
      });
  }
});

export const { 
  setSelectedChronicleId, 
  setSocketConnected, 
  setActiveFrequency, 
  setSlicingProtocolOpen 
} = heroSlice.actions;
export default heroSlice.reducer;
